import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import GameCertificate from "@/components/GameCertificate";
import { 
  Trophy, 
  Award, 
  Target, 
  Zap,
  Clock,
  Download,
  ExternalLink,
  ChevronRight,
  Loader2,
  Medal,
  Star,
  Flame,
  Lock,
  Unlock,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GameProfilePage({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [generatingCert, setGeneratingCert] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [historyRes, progressRes] = await Promise.all([
        axios.get(`${API}/game/student/${user.id}/history`),
        axios.get(`${API}/game/student/${user.id}/progress`)
      ]);
      setHistory(historyRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateCertificate = async (gameCode) => {
    setGeneratingCert(gameCode);
    try {
      const response = await axios.post(`${API}/game/generate-certificate`, {
        game_code: gameCode,
        player_id: user.id,
        business_name: `${user.name}'s Lemonade Stand`
      });
      
      if (response.data.success) {
        setSelectedCertificate(response.data.certificate);
        setShowCertificate(true);
      }
    } catch (error) {
      console.error("Failed to generate certificate:", error);
    } finally {
      setGeneratingCert(null);
    }
  };

  const generateBusinessPlan = async (gameCode) => {
    setGeneratingPlan(gameCode);
    try {
      const response = await axios.post(`${API}/game/generate-business-plan`, {
        game_code: gameCode,
        player_id: user.id,
        business_name: `${user.name}'s Lemonade Stand`
      });
      
      if (response.data.success) {
        setSelectedPlan(response.data);
      }
    } catch (error) {
      console.error("Failed to generate plan:", error);
    } finally {
      setGeneratingPlan(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🎮 My Game Profile</h1>
          <p className="text-gray-600">Track your progress, badges, and achievements</p>
        </div>

        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Trophy className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{history?.total_points || 0}</div>
            <div className="text-sm text-gray-500">Total Points</div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Target className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{history?.completed_modules?.length || 0}/3</div>
            <div className="text-sm text-gray-500">Modules Done</div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{history?.total_games || 0}</div>
            <div className="text-sm text-gray-500">Games Played</div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Award className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{history?.badges?.length || 0}</div>
            <div className="text-sm text-gray-500">Badges Earned</div>
          </div>
        </div>

        {/* Unlock Status */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <h2 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Unlock Progress
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-xl border-2 ${progress?.unlocked?.business_plan ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                {progress?.unlocked?.business_plan ? (
                  <Unlock className="w-6 h-6 text-green-600" />
                ) : (
                  <Lock className="w-6 h-6 text-gray-400" />
                )}
                <div>
                  <h3 className="font-bold text-gray-900">AI Business Plan</h3>
                  <p className="text-sm text-gray-500">
                    {progress?.unlocked?.business_plan 
                      ? "✓ Unlocked! Generate your personalized plan"
                      : "Complete all 3 modules to unlock"
                    }
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`p-4 rounded-xl border-2 ${progress?.unlocked?.certificate ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                {progress?.unlocked?.certificate ? (
                  <Unlock className="w-6 h-6 text-green-600" />
                ) : (
                  <Lock className="w-6 h-6 text-gray-400" />
                )}
                <div>
                  <h3 className="font-bold text-gray-900">Completion Certificate</h3>
                  <p className="text-sm text-gray-500">
                    {progress?.unlocked?.certificate 
                      ? "✓ Unlocked! Download your certificate"
                      : `Complete 3 modules + earn 500 points (${history?.total_points || 0}/500)`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Badges Section */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <h2 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
            <Medal className="w-5 h-5 text-yellow-500" />
            Badges Earned
          </h2>
          
          {history?.badges?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {history.badges.map((badge, idx) => (
                <div 
                  key={idx}
                  className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-4 text-center border border-yellow-200"
                >
                  <div className="text-4xl mb-2">{badge.icon}</div>
                  <h3 className="font-bold text-gray-900 text-sm">{badge.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{badge.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Medal className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No badges earned yet. Play games to earn badges!</p>
              <button
                onClick={() => navigate("/game/solo")}
                className="mt-4 text-yellow-600 hover:text-yellow-700 font-medium"
              >
                Start Playing →
              </button>
            </div>
          )}
        </div>

        {/* Game History */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <h2 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            Game History
          </h2>
          
          {history?.games?.length > 0 ? (
            <div className="space-y-3">
              {history.games.map((game, idx) => (
                <div 
                  key={idx}
                  className="border border-gray-200 rounded-xl p-4 hover:border-yellow-300 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">
                        {game.module_id === "module_1" && "💡"}
                        {game.module_id === "module_2" && "🦅"}
                        {game.module_id === "module_3" && "📋"}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{game.module_title}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span>{formatDate(game.completed_at)}</span>
                          {game.disc_type && (
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs">
                              {game.disc_type === 'eagle' && '🦅 Eagle'}
                              {game.disc_type === 'parrot' && '🦜 Parrot'}
                              {game.disc_type === 'dove' && '🕊️ Dove'}
                              {game.disc_type === 'owl' && '🦉 Owl'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xl font-bold text-yellow-600">{game.points}</div>
                        <div className="text-xs text-gray-500">points</div>
                      </div>
                      
                      <div className="flex gap-2">
                        {progress?.unlocked?.business_plan && (
                          <button
                            onClick={() => generateBusinessPlan(game.game_code)}
                            disabled={generatingPlan === game.game_code}
                            className="p-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors disabled:opacity-50"
                            title="Generate Business Plan"
                          >
                            {generatingPlan === game.game_code ? (
                              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                            ) : (
                              <Sparkles className="w-5 h-5 text-purple-600" />
                            )}
                          </button>
                        )}
                        {progress?.unlocked?.certificate && (
                          <button
                            onClick={() => generateCertificate(game.game_code)}
                            disabled={generatingCert === game.game_code}
                            className="p-2 bg-yellow-100 hover:bg-yellow-200 rounded-lg transition-colors disabled:opacity-50"
                            title="Download Certificate"
                          >
                            {generatingCert === game.game_code ? (
                              <Loader2 className="w-5 h-5 animate-spin text-yellow-600" />
                            ) : (
                              <Download className="w-5 h-5 text-yellow-600" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No games played yet</p>
              <button
                onClick={() => navigate("/game/solo")}
                className="mt-4 text-yellow-600 hover:text-yellow-700 font-medium"
              >
                Play Your First Game →
              </button>
            </div>
          )}
        </div>

        {/* Business Plan Modal */}
        {selectedPlan && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                <h2 className="font-bold text-xl text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                  Your AI Business Plan
                </h2>
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              </div>
              <div className="p-6">
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {selectedPlan.business_plan}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Modal */}
        {showCertificate && selectedCertificate && (
          <GameCertificate 
            certificate={selectedCertificate}
            onClose={() => {
              setShowCertificate(false);
              setSelectedCertificate(null);
            }}
          />
        )}

        {/* Quick Actions */}
        <div className="text-center">
          <button
            onClick={() => navigate("/game/solo")}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-8 py-4 rounded-full font-bold text-lg inline-flex items-center gap-2 transition-all"
          >
            🎮 Continue Playing
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </main>
    </div>
  );
}

export default GameProfilePage;
